import React, { Fragment, useEffect } from 'react';

import PosButton from '../components/PosButton';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Banner, BlockStack, Button, ButtonGroup, Card, InlineStack, Page, Text } from '@shopify/polaris';
import { useState } from 'react';
import usePosApi from '../hooks/Api';

function PreIssuedFormCompletePage() {
    const { id: preIssuedFormId } = useParams();
    const [customStatus, setCustomStatus] = useState(null);
    const [status, setStatus] = useState(null);
    const [barcode, setBarcode] = useState(null);
    const [receiptUrl, setReceiptUrl] = useState(null);
    const [voidUrl, setVoidUrl] = useState(null);
    const [refundMethod, setRefundMethod] = useState(null);
    const [refundAmount, setRefundAmount] = useState(null);
    const [fiscalNumber, setFiscalNumber] = useState(null);
    const [voidFiscalNumber, setVoidFiscalNumber] = useState(null);
    const [voidData, setVoidData] = useState(null);
    const [orders, setOrders] = useState([]);
    const { request, planetApi } = usePosApi();
    const navigate = useNavigate();
    const [voidError, setVoidError] = useState(null);

    async function loadData() {
        const {
            data: {
                planet_form_id,
                planet_form_status,
                planet_form_barcode,
                planet_receipt_url,
                planet_void_url,
                planet_refund_method,
                planet_refund_amount,
                fiscal_number,
                void_fiscal_number,
                orders,
                voidData,
            },
        } = await request(`pre-issued-forms/${preIssuedFormId}`);

        setStatus(planet_form_status);
        setBarcode(planet_form_barcode);
        setReceiptUrl(planet_receipt_url);
        setVoidUrl(planet_void_url);
        setRefundMethod(planet_refund_method);
        setRefundAmount(planet_refund_amount);
        setFiscalNumber(fiscal_number);
        setVoidFiscalNumber(voidFiscalNumber ?? null);
        setOrders(orders);
        setVoidData(voidData);
        setCustomStatus(null);
    }

    useEffect(() => {
        shopify.loading(true);
        loadData().then(() => {
            shopify.loading(false);
        });
    }, []);

    async function handlePrintAction() {
        open(receiptUrl, '_top');
    }

    async function handlePrintVoidAction() {
        open(voidUrl, '_top');
    }

    async function handleVoidAction() {
        if (confirm('Are you sure you want to void this TaxFree form?')) {
            setCustomStatus('Voiding TaxFree form...');
            setVoidError(null);

            const response = await planetApi.voidPreIssuedForm(preIssuedFormId);

            if (response.ok) {
                loadData();
            } else {
                setVoidError(response.data.message);
                setCustomStatus(null);
            }
        }
    }

    function formatVoidType(type) {
        const value = typeof type === 'string' ? type : type?.value;

        if (!value) {
            return null;
        }

        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    function formatDateTime(value) {
        if (!value) {
            return null;
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    }

    function VoidDataDetails() {
        if (!voidData) {
            return null;
        }

        const type = formatVoidType(voidData.type);
        const requestedAt = formatDateTime(voidData.requested_at);

        return (
            <Fragment>
                {type && (
                    <InlineStack gap={200} align="left">
                        <Text as="dt" fontWeight="bold">
                            Void Type:
                        </Text>
                        <Text as="dd">{type}</Text>
                    </InlineStack>
                )}
                {requestedAt && (
                    <InlineStack gap={200} align="left">
                        <Text as="dt" fontWeight="bold">
                            Void Request Sent At:
                        </Text>
                        <Text as="dd">{requestedAt}</Text>
                    </InlineStack>
                )}
                {voidData.exception && (
                    <InlineStack gap={200} align="left">
                        <Text as="dt" fontWeight="bold">
                            Void Error Message:
                        </Text>
                        <Text as="dd">{voidData.exception}</Text>
                    </InlineStack>
                )}
            </Fragment>
        );
    }

    function FormStatusComplete() {
        return (
            <BlockStack gap={400}>
                <Card>
                    <BlockStack gap={800}>
                        <BlockStack gap={100}>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Form Number:{' '}
                                </Text>
                                <Text as="dd">{preIssuedFormId}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Barcode:
                                </Text>
                                <Text as="dd">{barcode}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Fiscal Number:
                                </Text>
                                <Text as="dd">{fiscalNumber}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Status:{' '}
                                </Text>
                                <Badge progress="complete" tone="success">
                                    Complete
                                </Badge>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Refund Method:
                                </Text>
                                <Text as="dd">{refundMethod}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Refund Amount:{' '}
                                </Text>
                                <Text as="dd">{refundAmount}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Orders:{' '}
                                </Text>
                                <Text as="dd">{orders.join(', ')}</Text>
                            </InlineStack>
                            <VoidDataDetails />
                        </BlockStack>
                    </BlockStack>
                    <InlineStack align="end">
                        <ButtonGroup>
                            <Button variant="primary" onClick={() => handlePrintAction()} disabled={!receiptUrl}>
                                Print Receipt
                            </Button>
                        </ButtonGroup>
                    </InlineStack>
                </Card>
                <InlineStack align="center">
                    <Button variant="tertiary" tone="critical" onClick={() => handleVoidAction()}>
                        Void TaxFree form
                    </Button>
                </InlineStack>
            </BlockStack>
        );
    }

    function FormStatusVoided() {
        return (
            <BlockStack gap="400">
                <Card>
                    <BlockStack gap={800}>
                        <BlockStack gap={100}>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Form Number:{' '}
                                </Text>
                                <Text as="dd">{preIssuedFormId}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Barcode:{' '}
                                </Text>
                                <Text as="dd">{barcode}</Text>
                            </InlineStack>
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Fiscal Number:
                                </Text>
                                <Text as="dd">{fiscalNumber}</Text>
                            </InlineStack>
                            {voidFiscalNumber && (
                                <InlineStack gap={200} align="left">
                                    <Text as="dt" fontWeight="bold">
                                        Void Fiscal Number:
                                    </Text>
                                    <Text as="dd">{voidFiscalNumber}</Text>
                                </InlineStack>
                            )}
                            <InlineStack gap={200} align="left">
                                <Text as="dt" fontWeight="bold">
                                    Status:{' '}
                                </Text>
                                <Badge progress="complete" tone="critical">
                                    Voided
                                </Badge>
                            </InlineStack>
                            <VoidDataDetails />
                        </BlockStack>
                        {voidUrl && (
                            <InlineStack align="end">
                                <Button variant="primary" onClick={() => handlePrintVoidAction()}>
                                    Print Void
                                </Button>
                            </InlineStack>
                        )}
                    </BlockStack>
                </Card>
            </BlockStack>
        );
    }

    if (customStatus) {
        return (
            <Page backAction={{ content: 'Index', onAction: () => navigate(-1) }}>
                <Text>{customStatus}</Text>
            </Page>
        );
    }

    return (
        <Page
            narrowWidth
            title="Tax Free Form Information"
            backAction={{ content: 'Index', onAction: () => navigate(-1) }}
        >
            {voidError && (
                <Banner
                    tone="critical"
                    title="Error"
                    onDismiss={() => {
                        setVoidError(null);
                    }}
                >
                    <Text tone="critical">{voidError}</Text>
                </Banner>
            )}
            {!voidError && (
                <>
                    {'complete' === status && <FormStatusComplete />}
                    {'voided' === status && <FormStatusVoided />}
                </>
            )}
        </Page>
    );
}

export default PreIssuedFormCompletePage;
